import type { TokenDefinition } from '@/components/ui/TokenizedInput/types';
import { resolveUrl, verifyResponse } from '@/lib/api-utils';
import { resolveFilterTextToHumanReadable } from '@/lib/filters/resolveFilterText';
import AuthService from '@/services/AuthService';
import { Ticket } from '@certhon/models';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

interface TicketReportContextValue {
  /**
   * Current filtered ticket recnums available for PDF report generation.
   */
  filteredTicketRecnums: number[];

  /**
   * The filter text (for display in the PDF report).
   */
  filterText: string;

  /**
   * Register filtered tickets from a TicketTable.
   */
  registerFilteredTickets: (
    tickets: Ticket[],
    filterText: string,
    tokenDefinitions: TokenDefinition[],
  ) => void;

  /**
   * Generate and download PDF for the currently filtered tickets.
   */
  downloadFilteredTicketsPdf: () => Promise<void>;

  /**
   * Whether PDF generation is in progress.
   */
  isGeneratingPdf: boolean;
}

const TicketReportContext = createContext<TicketReportContextValue | null>(
  null,
);

interface TicketReportProviderProps {
  children: ReactNode;
  /** Project ID for report context */
  projectId?: number;
  /** Work ID for report context */
  workId?: number;
}

/**
 * Provider component that enables PDF report generation for filtered tickets.
 * Wrap project-details or work-details panels with this provider.
 */
export function TicketReportProvider({
  children,
  projectId,
  workId,
}: TicketReportProviderProps) {
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [filterText, setFilterText] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  // Use ref for tokenDefinitions to avoid re-renders (only needed at PDF generation time)
  const tokenDefinitionsRef = useRef<TokenDefinition[]>([]);

  const filteredTicketRecnums = useMemo(
    () => filteredTickets.map((t) => t.recnum!).filter(Boolean),
    [filteredTickets],
  );

  const registerFilteredTickets = useCallback(
    (tickets: Ticket[], text: string, tokenDefs: TokenDefinition[]) => {
      setFilteredTickets(tickets);
      setFilterText(text);
      tokenDefinitionsRef.current = tokenDefs;
    },
    [],
  );

  const downloadFilteredTicketsPdf = useCallback(async () => {
    if (filteredTicketRecnums.length === 0) {
      toast.warning('No tickets to include in the report');
      return;
    }

    setIsGeneratingPdf(true);

    const toastId = toast.loading(
      `Generating PDF for ${filteredTicketRecnums.length} ticket${filteredTicketRecnums.length > 1 ? 's' : ''}...`,
    );

    try {
      // Resolve filter text to human-readable form (e.g., label IDs → names)
      const humanReadableFilter = await resolveFilterTextToHumanReadable(
        filterText,
        tokenDefinitionsRef.current,
      );

      // Wait for auth to be ready (handles token refresh)
      await AuthService.getInstance().waitForReady();

      const response = await fetch(resolveUrl('/tickets/pdf'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          recnums: filteredTicketRecnums,
          projectId,
          workId,
          filterText: humanReadableFilter || undefined,
        }),
      });

      // Use verifyResponse to extract JSON error messages
      await verifyResponse(response);

      // Get the PDF blob and trigger download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      // Trigger download via anchor element (avoids popup blocker issues)
      const a = document.createElement('a');
      a.href = url;
      a.download = `tickets-report_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up after a delay
      setTimeout(() => URL.revokeObjectURL(url), 60000);

      toast.success('PDF downloaded successfully', { id: toastId });
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      toast.error(
        `Failed to generate PDF: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { id: toastId },
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [filteredTicketRecnums, filterText, projectId, workId]);

  const value = useMemo(
    () => ({
      filteredTicketRecnums,
      filterText,
      registerFilteredTickets,
      downloadFilteredTicketsPdf,
      isGeneratingPdf,
    }),
    [
      filteredTicketRecnums,
      filterText,
      registerFilteredTickets,
      downloadFilteredTicketsPdf,
      isGeneratingPdf,
    ],
  );

  return (
    <TicketReportContext.Provider value={value}>
      {children}
    </TicketReportContext.Provider>
  );
}

/**
 * Hook to access the ticket report context.
 * Must be used within a TicketReportProvider.
 */
export function useTicketReportContext(): TicketReportContextValue | null {
  return useContext(TicketReportContext);
}

/**
 * Hook for TicketTable to register its filtered data.
 * Safe to use outside of a provider (will be a no-op).
 */
export function useRegisterFilteredTickets() {
  const context = useContext(TicketReportContext);

  return useCallback(
    (
      tickets: Ticket[],
      filterText: string,
      tokenDefinitions: TokenDefinition[],
    ) => {
      context?.registerFilteredTickets(tickets, filterText, tokenDefinitions);
    },
    [context],
  );
}
